import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, forwardRef, Self, AfterViewInit, Optional, Host, SimpleChanges,  } from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor, UntypedFormControl, Validators, NgControl, NG_VALIDATORS, FormGroup, FormBuilder, AbstractControl, ValidationErrors, ControlContainer} from '@angular/forms'
import { GoogleMapSearchResult } from '../model/google-map-result';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import { GoogleMapSearchService } from '../google-map-search.service';
import { take, tap, catchError } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, pipe } from 'rxjs';
import { ResourceService } from 'src/app/services/resource.service';
import { LocationDetailsResponse } from 'src/app/shared/models/LocationDetailsResponse.model';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { LocationToStringPipe } from 'src/app/shared/pipes/location-to-string.pipe';
import { AutoComplete } from 'primeng/autocomplete';

interface AutoCompleteCompleteEvent {
  originalEvent: Event;
  query: string;
}
@Component({
  selector: 'terra-google-map-search-input',
  templateUrl: './google-map-search-input.component.html',
  styleUrls: ['./google-map-search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GoogleMapSearchInputComponent),
      multi: true
    },
    {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => GoogleMapSearchInputComponent),
        multi: true
    }
  ]
})
export class GoogleMapSearchInputComponent extends OnDestroyMixin implements OnInit, ControlValueAccessor, AfterViewInit  {
  
  @Input() adressType: string;
  @Input() delayMSBetweenUserKeyStrokes: number = 20000;
  @Input() isOuterFormSubmitted: boolean = false;

  @Output() setAddress: EventEmitter<GoogleMapSearchResult> = new EventEmitter<GoogleMapSearchResult>();
  @ViewChild('mapContainer') mapContainer: ElementRef;
  @ViewChild('autocomplete') autocomplete: AutoComplete;

  @Input()
  addressLabelText = 'Address (Country, State, City, Street and Number)';

  @Input()
  addressSubLabelText = 'You may type in any language';

  @Input()
  placeholderText = 'Please enter the address';

  @Input()
  requiredErrorText = 'Country is required';

  @Input()
  invalidErrorText = 'The address is not valid';

  @Input()
  shouldShowMap: boolean = true;

  public addressForm: FormGroup;

  @Input()
  searchTypes: string[] = []
  
  @Input()
  restrictedCountries: string[];
  
  private _disabled = false;
  private onChange: (value: {address: LocationDetailsResponse, street2Name: string}) => void;
  private onTouched: () => void;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.addressForm.disable() : this.addressForm.enable();
  }

  map: google.maps.Map;

  constructor(private googleMapsService: GoogleMapSearchService, private resourceService: ResourceService, private locationToString : LocationToStringPipe, private formBuilder: FormBuilder) {
    super();
  }

  filteredAddresses$ : BehaviorSubject<LocationDetailsResponse[]> = new BehaviorSubject<LocationDetailsResponse[]>([]);



  writeValue(obj: any): void {
    const form = {
      address: obj?.address,
      street2Name: obj?.address?.street2Name
  };
  combineLatest([this.filteredAddresses$,this.resourceService.allCountries$]).pipe(take(1),tap(([addresses, countries]) => {
    if(!addresses.length && obj?.address) {
      const locationDetailsClone = {...obj?.address};
      if(!locationDetailsClone.country && locationDetailsClone.countryId) {
        locationDetailsClone.country = countries.find(c => c.id == locationDetailsClone.countryId); 
      }
      if(!locationDetailsClone.fullAddress) {
        const fields = [[locationDetailsClone.houseNumber, locationDetailsClone.streetName], [locationDetailsClone.cityName], [locationDetailsClone.stateName], [locationDetailsClone.postalCode], [locationDetailsClone.cityName || locationDetailsClone.stateName || locationDetailsClone.postalCode ? locationDetailsClone.country.code : locationDetailsClone.country.name]];
        locationDetailsClone.fullAddress = fields.map(part => part.filter(Boolean).join(" "))
        .filter(str => str.length).join(", ");
      }
      if(locationDetailsClone.fullAddress != '' && !locationDetailsClone.googlePlaceId) {
        this.filteredAddresses$.next([locationDetailsClone]);
      }
      form.address = locationDetailsClone;
    }
    if(this.map && form.address?.lat && form.address?.lng) {
      this.addAddressToTheMap(null, form.address);
    }
    this.addressForm.patchValue(form, { emitEvent: false });
    this.addressForm.updateValueAndValidity();
  })).subscribe();
  }
  registerOnChange(fn: (value: {address: LocationDetailsResponse, street2Name: string}) => void): void {
    this.addressForm.valueChanges.subscribe(fn);
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit() {
    this.addressForm = this.formBuilder.group({
      address: [null, Validators.required],
      street2Name: null,
  });
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes['isOuterFormSubmitted'] && changes['isOuterFormSubmitted'].previousValue === false) {
      Object.values(this.addressForm.controls).forEach(control => {control.markAsTouched(); control.markAsDirty()});
    }
}

  validate(control: AbstractControl): ValidationErrors {
    return this.addressForm.valid ? null : { invalidForm: { valid: false, errors: this.addressForm.errors } };
}

registerOnValidatorChange?(fn: () => void): void { }

  ngAfterViewInit() {
    if(this.shouldShowMap) {
      this.map = new google.maps.Map(
        this.mapContainer.nativeElement,
        {
          zoom: 1,
          center: { lat: 35.141927, lng: -40.480495 },
        }
      );
      const value = this.addressForm.getRawValue();
      if(value?.address?.lat && value?.address?.lng) {
        this.addAddressToTheMap(null, value?.address);
      }
    }
    // this.autocomplete.onOverlayAnimationEnd = (event)=>{};
  }

  invokeEvent(place: google.maps.places.PlaceResult) {
    if(!place.place_id || !place.geometry) {
      return;
    }
    const googleMapResult = this.parseGoogleResponse(place);
    const value = {...this.addressForm.getRawValue()};
    this.resourceService.allCountries$.pipe(take(1),tap(countries => {
      const country = countries.find(c => c.name === googleMapResult.country);
      const newAddress = {streetName: googleMapResult.address, 
        houseNumber: googleMapResult.streetNumber, 
        cityName: googleMapResult.city, 
        postalCode: googleMapResult.postalCode, 
        stateName: googleMapResult.state, 
        country: country, 
        countryId: country?.id,
        fullAddress: googleMapResult.fullAddress,
        googlePlaceId: place.place_id,
        street2Name: value.street2Name,
      lat: googleMapResult.lat,
    lng: googleMapResult.lng } as LocationDetailsResponse;
    this.addressForm.patchValue({address: newAddress }, { emitEvent: false });
    this.addressForm.updateValueAndValidity();
    this.addAddressToTheMap(value.address, newAddress);
    value.address = newAddress
    this.setAddress.emit(googleMapResult);
    })).subscribe()

  }

  addAddressToTheMap(oldAddress: LocationDetailsResponse, address: LocationDetailsResponse) {
    if(oldAddress && oldAddress.googlePlaceId && oldAddress.lat && oldAddress.lng) {
      this.map.data.remove(this.map.data.getFeatureById(oldAddress.googlePlaceId))
    }
    this.map.data.add({geometry: {lat: address.lat, lng: address.lng}, id: address.googlePlaceId, properties: address});
    this.map.setCenter({lat: address.lat, lng: address.lng});
    this.map.setZoom(10);
  }
  
  addressSelected(event: LocationDetailsResponse) {
    this.googleMapsService.getPlaceDetails(event.googlePlaceId, ['address_components', 'formatted_address', 'geometry', 'place_id']).pipe(take(1),tap(result => this.invokeEvent(result))).subscribe();
  }

  filterAddress(event: AutoCompleteCompleteEvent) {
      if(event.query?.length < 3) {
        this.filteredAddresses$.next([]);
        return;
      }
      this.googleMapsService.textSearch(event.query, this.searchTypes, this.restrictedCountries).pipe(take(1),tap(results => this.filteredAddresses$.next(results.map(result => ({fullAddress: result.description, googlePlaceId: result.place_id} as LocationDetailsResponse)))),catchError(err => {this.filteredAddresses$.next([]); throw err;})).subscribe();
  }

  parseGoogleResponse(result: google.maps.places.PlaceResult): GoogleMapSearchResult
  {
      // Extracting address components
        let googleMapSearchResult = new GoogleMapSearchResult();

        if(!Array.isArray(result.address_components))
        {
          throw new Error("Address component is not an array")
        }

        if(!result.address_components.length)
        {
          throw new Error("Address component is not an array")
        }

        for (let i = 0; i < result.address_components.length; i++) 
        {
          const component = result.address_components[i];
    
          if (this.isStreetNumber(component)) {
            googleMapSearchResult.streetNumber = component.long_name;
          }
    
          if (this.isStreetName(component)) {
            googleMapSearchResult.address = component.long_name;
          }
    
          if (this.isCity(component)) {
            googleMapSearchResult.city = component.long_name;
          }
    
          if (this.isCountry(component)) {
            googleMapSearchResult.country = component.long_name;
            googleMapSearchResult.countryCode = component.short_name;
          }
    
          if  (this.isState(component)) {
            googleMapSearchResult.state = component.long_name;
          }
    
          if (this.isPostalCode(component)) {
            googleMapSearchResult.postalCode = component.long_name;
          }
        }
      
      // Extracting additional information
      googleMapSearchResult.fullAddress = result.formatted_address;
      if (result.geometry && result.geometry.location) {
        googleMapSearchResult.lat = result.geometry.location.lat();
        googleMapSearchResult.lng = result.geometry.location.lng();
      }
      
      return googleMapSearchResult;
    
  }

  private isStreetNumber(component: any): boolean {
    return component.types.includes('street_number');
  }

  private isStreetName(component: any): boolean {
    return component.types.includes('route');
  }

  private isCity(component): boolean {
    return component.types.includes('locality') || component.types.includes('sublocality_level_1');
  }

  private isState(component): boolean {
    return component.types.includes('administrative_area_level_1');
  }

  private isCountry(component): boolean {
    return component.types.includes('country');
  }

  private isPostalCode(component): boolean {
    return component.types.includes('postal_code');
  }
}